import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { Permissions } from 'types';
import { SizeScale, SizeScaleDiff } from 'types/api/sizeScales.type';

import { useAppSelector } from 'redux/hooks';

import { sizeScalesDataService } from 'services/sizeScales.service';

import { plural } from 'helpers/plural';

type ImportPreview = { file: File; diff: SizeScaleDiff };

const useSizeScales = () => {
  const isAdmin = useAppSelector((state) =>
    Boolean(state.currentUser.user?.permissions.includes(Permissions.ADMIN))
  );

  const [scales, scalesSet] = useState<SizeScale[]>([]);
  const [isLoading, isLoadingSet] = useState(false);
  const [preview, previewSet] = useState<ImportPreview>();
  const [isBusy, isBusySet] = useState(false);

  const load = useCallback(() => {
    isLoadingSet(true);
    sizeScalesDataService
      .getAll()
      .then((res) => scalesSet(res.data))
      .catch(() => {
        // the http-common interceptor already shows the error toast
      })
      .finally(() => isLoadingSet(false));
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // lets the same file be picked again after a cancel
    e.target.value = '';
    if (!file) return;

    isBusySet(true);
    try {
      const res = await sizeScalesDataService.previewImport(file);
      previewSet({ file, diff: res.data });
    } catch {
      // the http-common interceptor already shows the backend's message
    } finally {
      isBusySet(false);
    }
  };

  const apply = async () => {
    if (!preview) return;

    isBusySet(true);
    try {
      const { data } = await sizeScalesDataService.applyImport(preview.file);
      const rejected = data.summary.rejected
        ? `, ${plural(data.summary.rejected, 'row')} rejected`
        : '';

      if (data.written) {
        toast.success(
          `Size scales updated: ${plural(data.written, 'size')} written${rejected}`
        );
      } else {
        toast(`No size changed${rejected}`);
      }
      previewSet(undefined);
      load();
    } catch {
      // the http-common interceptor already shows the backend's message
    } finally {
      isBusySet(false);
    }
  };

  // Closing the preview cannot stop an apply already sent: keep it open until the write is done.
  const cancel = () => {
    if (!isBusy) previewSet(undefined);
  };

  return { isAdmin, scales, isLoading, preview, isBusy, onFileChange, apply, cancel };
};

export { useSizeScales };
